<template>
  <div class="result-view">
    <v-container>
      <!-- Top bar -->
      <v-row>
        <v-col cols="12" class="d-flex align-center flex-wrap">
          <v-btn text @click="$router.push('/')">
            <v-icon left>mdi-arrow-left</v-icon>
            Process another image
          </v-btn>
          <v-spacer></v-spacer>
          <span v-if="image" class="text-caption result-meta mr-4">
            {{ image.original_filename }} · {{ resultMeta }}
          </span>
          <v-btn
            v-if="image && image.status === 'complete'"
            small
            outlined
            color="primary"
            class="mr-2"
            :loading="sharing"
            :disabled="!resultBlobUrl || !originalBlobUrl"
            @click="shareResult"
          >
            <v-icon left small>mdi-share-variant</v-icon>
            Share
          </v-btn>
          <v-btn
            v-if="image && image.status === 'complete'"
            color="primary"
            depressed
            :disabled="!resultBlobUrl"
            @click="downloadResult"
          >
            <v-icon left>mdi-download</v-icon>
            Download
          </v-btn>
        </v-col>
      </v-row>

      <!-- Members who just spent their last free conversion (and can't
           afford another) are looking at their best result - the one moment
           the offer earns a dedicated section: packs and plans. -->
      <ResultOffer
        v-if="image && image.status === 'complete' && freeNote && freeNote.pack"
      />

      <!-- Guests who used their free conversion: the same strip, selling
           the free account (+1 conversion, Google one-click). -->
      <ResultOffer
        v-else-if="image && image.status === 'complete' && freeNote && freeNote.signup"
        mode="signup"
      />

      <!-- Free conversions left after this one -->
      <v-row v-else-if="image && image.status === 'complete' && freeNote" dense>
        <v-col cols="12" class="text-body-2 free-note">
          <v-icon small color="success" class="mr-1">mdi-gift-outline</v-icon>
          {{ freeNote.text }}
        </v-col>
      </v-row>

      <!-- Free results download with a small watermark; the server decides
           (image.watermarked) and any purchase clears it account-wide. -->
      <v-row v-if="image && image.status === 'complete' && image.watermarked" dense>
        <v-col cols="12" class="text-body-2 free-note">
          <v-icon small color="amber darken-2" class="mr-1">mdi-water-outline</v-icon>
          Free results include a small watermark - any pack or plan removes it
          from all your images instantly.
        </v-col>
      </v-row>

      <!-- Initial fetch -->
      <v-row v-if="loading">
        <v-col cols="12" class="text-center py-12">
          <v-progress-circular indeterminate color="primary" />
        </v-col>
      </v-row>

      <!-- Not found -->
      <v-row v-else-if="notFound">
        <v-col cols="12">
          <v-card outlined class="text-center py-12">
            <v-icon size="48" color="primary">mdi-image-off-outline</v-icon>
            <p class="text-body-1 mt-3 mb-4">This image doesn't exist.</p>
            <v-btn color="primary" to="/">Upload an image</v-btn>
          </v-card>
        </v-col>
      </v-row>

      <template v-else-if="image">
        <v-alert v-if="recoveryMessage" type="info" outlined>{{ recoveryMessage }}</v-alert>
        <!-- Processing: animated scan loader -->
        <v-row v-if="isProcessing">
          <v-col cols="12">
            <v-card outlined class="processing-card">
              <v-card-text>
                <div class="scan-wrap">
                  <img
                    v-if="originalBlobUrl"
                    :src="originalBlobUrl"
                    class="scan-image"
                    alt="Image being enhanced"
                  />
                  <div v-else class="scan-placeholder"></div>
                  <div class="scan-shimmer"></div>
                  <div class="scan-line"></div>
                </div>
                <div class="mt-4 text-center">
                  <div class="text-subtitle-1">
                    {{ processingMessage }}
                  </div>
                </div>
                <div v-if="progressPercent !== null" class="mt-3">
                  <v-progress-linear
                    :value="progressPercent"
                    height="18"
                    rounded
                    color="primary"
                  >
                    <span class="progress-pct">{{ Math.round(progressPercent) }}%</span>
                  </v-progress-linear>
                  <div class="d-flex justify-space-between text-caption processing-hint mt-1">
                    <span>{{ progressHint }}</span>
                    <span>{{ progressLabel }}</span>
                  </div>
                </div>
                <div v-else class="text-caption processing-hint mt-1 text-center">
                  Adding realistic detail - this can take a minute for large
                  images. You can safely refresh or come back later.
                </div>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>

        <!-- Complete: before/after comparison -->
        <v-row v-else-if="image.status === 'complete'">
          <v-col cols="12">
            <template v-if="originalBlobUrl && resultBlobUrl">
              <!-- The on-image zoom pill is easy to miss; the first things a
                   viewer reaches for live here as plain buttons. -->
              <div class="d-flex flex-wrap mb-2 view-actions">
                <v-btn small depressed color="primary" class="mr-2 mb-1" @click="viewFullImage">
                  <v-icon left small>mdi-arrow-expand-all</v-icon>
                  View full image
                </v-btn>
                <v-btn small depressed color="primary" class="mr-2 mb-1" @click="viewDetail">
                  <v-icon left small>mdi-magnify-plus-outline</v-icon>
                  Zoom to detail
                </v-btn>
                <v-btn small depressed color="primary" class="mb-1" @click="openInNewTab">
                  <v-icon left small>mdi-open-in-new</v-icon>
                  Open in new tab
                </v-btn>
              </div>
              <compare-slider
                ref="compare"
                :before-src="originalBlobUrl"
                :after-src="resultBlobUrl"
              />
              <div class="compare-hint mt-2">
                Opened on the rebuilt detail - press Fit for the whole image ·
                scroll to zoom · drag to pan · drag the divider to compare
              </div>
            </template>
            <!-- Result file still downloading (full-resolution output can be
                 several MB): keep the animated card instead of a grey box. -->
            <v-card v-else outlined class="processing-card">
              <v-card-text>
                <div class="scan-wrap">
                  <img
                    v-if="originalBlobUrl"
                    :src="originalBlobUrl"
                    class="scan-image"
                    alt="Image being loaded"
                  />
                  <div v-else class="scan-placeholder"></div>
                  <div class="scan-shimmer"></div>
                </div>
                <div class="mt-4 text-center">
                  <div class="text-subtitle-1">Downloading your result…</div>
                </div>
                <v-progress-linear
                  class="mt-3"
                  height="18"
                  rounded
                  color="primary"
                  :value="resultLoadPct === null ? 100 : resultLoadPct"
                  :indeterminate="resultLoadPct === null"
                >
                  <span v-if="resultLoadPct !== null" class="progress-pct">
                    {{ Math.round(resultLoadPct) }}%
                  </span>
                </v-progress-linear>
                <div class="text-caption processing-hint mt-1 text-center">
                  Full-resolution results are large - almost there.
                </div>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>

        <!-- Needs credits -->
        <v-row v-if="outOfCreditsMessage">
          <v-col cols="12">
            <v-alert type="warning" outlined class="mb-0">
              {{ outOfCreditsMessage }}
              <div class="mt-2">
                <v-btn small color="primary" to="/subscribe">View plans</v-btn>
                <v-btn small text class="ml-2" :loading="retrying" @click="retry">
                  Retry now
                </v-btn>
              </div>
            </v-alert>
          </v-col>
        </v-row>

        <!-- Failure -->
        <v-row v-if="failureMessage">
          <v-col cols="12">
            <v-alert type="error" outlined class="mb-0">
              {{ failureMessage }}
              <div class="mt-2">
                <!-- Safety rejections can't succeed on retry (we already
                     tried 3x) - offer the non-generative fallback instead. -->
                <v-btn
                  v-if="safetyRejected"
                  small
                  color="primary"
                  depressed
                  :loading="fallbackRunning"
                  @click="fallbackUpscale"
                >
                  Upscale without realism instead
                </v-btn>
                <v-btn v-else small text :loading="retrying" @click="retry">
                  Retry
                </v-btn>
              </div>
              <div v-if="safetyRejected" class="text-caption mt-1">
                Skips the realism step entirely - classic upscaling of your
                original image.
              </div>
            </v-alert>
          </v-col>
        </v-row>
      </template>
    </v-container>
  </div>
</template>

<script>
import { apiClient, createGuestClient } from "@shared/services/api";
import { workResumeAction, workFundingHeaders, workPollingDelay, SETTLEMENT_GRACE_MS } from "@shared/utils/workBilling";
import { fetchFileBlob } from "@shared/utils/fileBlob";
import { pendingGuests } from "@shared/services/session";
import { readJson } from "../../../../../shared-client/guestSession.mjs";
import CompareSlider from "../components/CompareSlider.vue";
import { composeBeforeAfter } from "../utils/shareImage";
import { CONVERSION_CREDITS } from "@/config";
import ResultOffer from "../components/ResultOffer.vue";

// settlement_pending is not terminal: the server settles right after staging
// the result, so polling continues (see SETTLEMENT_GRACE_MS).
const TERMINAL_STATUSES = ["complete", "failed", "cancelled", "needs_credits"];

// Everything on this page derives from the :id route param via the API, so
// a refresh (or a Gallery click-through) lands on the same live view.
export default {
  name: "ResultView",
  components: { CompareSlider, ResultOffer },
  data() {
    return {
      image: null,
      guestProof: null,
      guestClient: null,
      claiming: false,
      loading: true,
      notFound: false,

      pollTimer: null,
      pollDelay: null,
      sharing: false,

      // Progress bar: server-measured elapsed/expected seconds, smoothed
      // locally between polls and never moving backwards.
      progressBase: null,
      progressPolledAt: null,
      progressNow: Date.now(),
      progressPeak: 0,
      progressTicker: null,

      // Result-file download: real transfer progress when the server sends
      // a length, indeterminate otherwise. Guard prevents the terminal poll
      // from starting the multi-MB downloads twice.
      blobsLoading: false,
      resultLoadPct: null,
      settlementSince: null,
      settlementStalled: false,
      recoveryMessage: null,
      retrying: false,

      originalBlobUrl: null,
      resultBlobUrl: null,

      outOfCreditsMessage: null,
      failureMessage: null,
      fallbackRunning: false
    };
  },
  computed: {
    imageId() {
      return this.$route.params.id;
    },
    // Free conversions left after this one; guests who used theirs are
    // offered the sign-up bonus instead.
    // The realism step rejected this image on every internal attempt;
    // retrying the same settings cannot work.
    safetyRejected() {
      return !!(this.failureMessage && /safety system/i.test(this.failureMessage));
    },
    freeNote() {
      const value = this.$store.getters.freeAllowance;
      if (!value) return null;
      const left = Math.floor(Number(value.remaining) || 0);
      if (left > 0) {
        return { text: `You have ${left} free conversion${left === 1 ? "" : "s"} left.`, signup: false };
      }
      const user = this.$store.getters.currentUser;
      if (user && user.is_anonymous) return { text: "That was your free conversion.", signup: true };
      // Members out of free conversions get the pack offer - unless their
      // credit balance already covers a typical conversion (then it's noise).
      const balance = this.$store.getters.creditBalance;
      const credits = balance ? Number(balance.balance) || 0 : 0;
      if (credits < CONVERSION_CREDITS.high) {
        return { text: "That was your last free conversion.", pack: true };
      }
      return null;
    },
    isProcessing() {
      if (!this.image) return false;
      if (this.image.status === "settlement_pending") return !this.settlementStalled;
      return ["uploaded", "processing"].indexOf(this.image.status) !== -1;
    },
    processingMessage() {
      if (this.image && this.image.status === "settlement_pending") return "Finalizing your result…";
      return (this.image && this.image.job_status_message) || "Enhancing your image…";
    },
    expectedSeconds() {
      const value = this.image && this.image.expected_seconds;
      return value && value > 0 ? value : null;
    },
    progressElapsed() {
      if (this.progressBase === null || !this.progressPolledAt) return null;
      return this.progressBase + Math.max(0, (this.progressNow - this.progressPolledAt) / 1000);
    },
    progressPercent() {
      if (!this.isProcessing || this.progressElapsed === null || !this.expectedSeconds) {
        return null;
      }
      return this.progressPeak;
    },
    progressOvertime() {
      return this.progressElapsed !== null && this.expectedSeconds &&
        this.progressElapsed > this.expectedSeconds * 1.4;
    },
    progressHint() {
      return this.progressOvertime
        ? "Taking a little longer than usual - still working…"
        : "You can safely refresh or come back later.";
    },
    progressLabel() {
      const fmt = total => {
        const s = Math.max(0, Math.round(total));
        return Math.floor(s / 60) + ":" + String(s % 60).padStart(2, "0");
      };
      if (this.progressElapsed === null || !this.expectedSeconds) return "";
      return fmt(this.progressElapsed) + " / ~" + fmt(this.expectedSeconds);
    },
    resultMeta() {
      const parts = [];
      const settings = this.image && this.image.settings;
      // Legacy rows stored a boolean realism_boost instead of a level.
      let level = settings && settings.realism;
      if (!level && settings && settings.realism_boost !== undefined) {
        level = settings.realism_boost ? "high" : "none";
      }
      const labels = {
        none: "Upscale only",
        basic: "Basic realism",
        high: "High realism",
        extreme: "Extreme realism"
      };
      if (labels[level]) parts.push(labels[level]);
      parts.push(this.image.scale + "x");
      return parts.join(" · ");
    }
  },
  watch: {
    // Route reuse with a different id (e.g. history navigation).
    imageId() {
      this.resetState();
      this.restorePendingClaim();
      this.load();
    }
  },
  async created() {
    this.restorePendingClaim();
    await this.tryClaim();
    this.load();
  },
  beforeDestroy() {
    this.stopPolling();
    this.stopProgressTicker();
    this.revokeObjectUrls();
  },
  methods: {
    restorePendingClaim() {
      this.guestProof = readJson(pendingGuests, "/images/" + this.imageId);
      const proof = this.guestProof;
      this.guestClient = proof ? createGuestClient(proof.access_token, token => {
        if (this.guestProof !== proof) return;
        this.guestProof.access_token = token;
        pendingGuests.setItem("/images/" + this.imageId, JSON.stringify(this.guestProof));
      }) : null;
    },
    resourceClient() {
      return this.guestClient || apiClient;
    },
    async tryClaim() {
      if (!this.guestProof || this.claiming) return;
      const user = this.$store.getters.currentUser;
      if (!user || user.is_anonymous) return;
      this.claiming = true;
      try {
        await apiClient.post(`/apps/upscale/images/${this.imageId}/claim`, {
          guest_token: this.guestProof.access_token
        }, { skipAuthRecovery: true });
        pendingGuests.removeItem("/images/" + this.imageId);
        this.guestProof = null;
        this.guestClient = null;
      } catch (e) {
        // 409 means work is still active. Keep proof and view/poll with the
        // isolated guest client; no failure adopts it as the member session.
      } finally {
        this.claiming = false;
      }
    },
    async load() {
      this.loading = true;
      try {
        const response = await this.resourceClient().get(
          `/apps/upscale/images/${this.imageId}`
        );
        if (TERMINAL_STATUSES.indexOf(response.data.status) !== -1) await this.tryClaim();
        this.applyImage(response.data);
        if (this.guestProof) this.startPolling();
        // Arriving from the Gallery's Retry button: kick the run off here
        // so the user lands straight on the progress view.
        if (
          this.$route.query.retry &&
          this.image &&
          ["failed", "needs_credits"].indexOf(this.image.status) !== -1
        ) {
          this.retry();
        }
      } catch (e) {
        this.notFound = true;
      } finally {
        this.loading = false;
      }
    },

    applyImage(image) {
      this.image = image;
      if (typeof image.elapsed_seconds === "number") {
        this.progressBase = image.elapsed_seconds;
        this.progressPolledAt = Date.now();
      }
      if (image.status === "settlement_pending") {
        if (!this.settlementSince) this.settlementSince = Date.now();
        this.settlementStalled = Date.now() - this.settlementSince >= SETTLEMENT_GRACE_MS;
      } else {
        this.settlementSince = null;
        this.settlementStalled = false;
      }
      this.outOfCreditsMessage = null;
      this.failureMessage = null;
      this.recoveryMessage = null;
      // This page is showing the outcome itself - the app-wide "image
      // ready" toast is only for users who navigated away mid-conversion.
      if (TERMINAL_STATUSES.indexOf(image.status) !== -1) {
        this.$store.commit("CLEAR_ACTIVE_WORK", this.activeWorkKey());
      }
      if (image.status === "recovery_required") {
        this.recoveryMessage = image.work && image.work.message || "This work needs review before it can safely continue.";
        this.startPolling();
        return;
      }
      if (this.isProcessing) {
        if (!this.originalBlobUrl) this.loadOriginal();
        this.startPolling();
        this.registerActiveWork();
        this.startProgressTicker();
      } else if (image.status === "complete") {
        if (!this.resultBlobUrl) this.loadBlobs();
      } else if (image.status === "settlement_pending") {
        // Stalled past the grace period: offer the retry, but keep checking.
        this.outOfCreditsMessage = "Your result is ready. Payment confirmation is pending; retry to check again.";
        if (!this.originalBlobUrl) this.loadOriginal();
        this.startPolling();
      } else if (image.status === "needs_credits") {
        this.outOfCreditsMessage =
          "You're out of credits. Top up or upgrade your plan to continue.";
        if (!this.originalBlobUrl) this.loadOriginal();
      } else if (image.status === "failed" || image.status === "cancelled") {
        this.failureMessage = image.error || (image.status === "cancelled" ? "The conversion was cancelled." : "The enhancement failed.");
        if (!this.originalBlobUrl) this.loadOriginal();
      }
    },

    // ---------------------------------------------------------------
    // Polling
    // ---------------------------------------------------------------
    startPolling() {
      const delay = workPollingDelay(this.image) || (this.guestProof ? 30000 : null);
      if (!delay) return;
      if (this.pollTimer && this.pollDelay === delay) return;
      this.stopPolling();
      this.pollDelay = delay;
      this.pollTimer = setInterval(this.pollOnce, delay);
    },
    stopPolling() {
      if (this.pollTimer) {
        clearInterval(this.pollTimer);
        this.pollTimer = null;
      }
    },
    startProgressTicker() {
      if (this.progressTicker) return;
      this.progressTicker = setInterval(() => {
        this.progressNow = Date.now();
        if (!this.isProcessing) {
          this.stopProgressTicker();
          return;
        }
        if (this.progressElapsed !== null && this.expectedSeconds) {
          const pct = Math.min(100, (100 * this.progressElapsed) / this.expectedSeconds);
          if (pct > this.progressPeak) this.progressPeak = pct;
        }
      }, 300);
    },
    stopProgressTicker() {
      if (this.progressTicker) {
        clearInterval(this.progressTicker);
        this.progressTicker = null;
      }
    },
    async pollOnce() {
      try {
        const response = await this.resourceClient().get(
          `/apps/upscale/images/${this.imageId}`
        );
        const image = response.data;
        this.image = image;
        this.applyImage(image);
        if (TERMINAL_STATUSES.indexOf(image.status) !== -1) {
          await this.tryClaim();
          if (!this.guestProof) this.stopPolling();
          // The run charged (or paused on) credits - refresh the badge.
          this.$store.dispatch("fetchCreditBalance");
          this.applyImage(image);
        }
      } catch (e) {
        // Transient polling error - leave the timer running, it will retry.
      }
    },

    // ---------------------------------------------------------------
    // Retry (after failure or top-up)
    // ---------------------------------------------------------------
    async retry() {
      if (workResumeAction(this.image) === "blocked") return;
      await this.tryClaim();
      if (this.guestProof) {
        this.failureMessage = "Sign in and wait for the current conversion to finish before retrying.";
        return;
      }
      this.retrying = true;
      this.outOfCreditsMessage = null;
      this.failureMessage = null;
      try {
        if (workResumeAction(this.image) === "settle") {
          await apiClient.post(`/jobs/${this.image.job_id}/settle`);
          const refreshed = await apiClient.get(`/apps/upscale/images/${this.imageId}`);
          this.applyImage(refreshed.data);
          return;
        }
        const settings = this.image.settings || {};
        const estimate = await apiClient.get("/apps/upscale/estimate", { params: {
          width: this.image.width, height: this.image.height,
          scale: this.image.scale, realism: settings.realism || "high"
        } });
        if (!estimate.data.trial && Number(estimate.data.credits) > 0 &&
            !window.confirm(`Retry this conversion for ${estimate.data.credits} credits?`)) {
          this.applyImage(this.image);
          return;
        }
        const response = await apiClient.post(
          `/apps/upscale/images/${this.imageId}/run`, null,
          { headers: workFundingHeaders(estimate.data) }
        );
        this.applyImage(response.data.image || response.data);
      } catch (e) {
        if (e.response && e.response.status === 402) {
          const detail = e.response.data && e.response.data.detail;
          this.outOfCreditsMessage =
            (detail && (detail.message || detail)) ||
            "You're out of credits. Top up or upgrade your plan to continue.";
        } else {
          const detail = e.response && e.response.data && e.response.data.detail;
          this.failureMessage = (e.workPending && "We couldn't confirm the retry yet. Try again with the same settings to resume it safely.") || (detail && (detail.message || detail)) ||
            "Something went wrong while restarting the enhancement.";
        }
      } finally {
        this.retrying = false;
      }
    },

    // Safety-rejection fallback: the same upload, realism off, on the
    // non-generative engine (classic upscaling, no detail synthesis).
    // Failed jobs released their reservation, so a free user's trial
    // conversion funds this; paid users pay the resolution-only price.
    async fallbackUpscale() {
      if (workResumeAction(this.image) === "blocked") return;
      await this.tryClaim();
      if (this.guestProof) {
        this.failureMessage =
          "Sign in and wait for the current conversion to finish before retrying.";
        return;
      }
      this.fallbackRunning = true;
      this.outOfCreditsMessage = null;
      try {
        const estimate = await apiClient.get("/apps/upscale/estimate", { params: {
          width: this.image.width, height: this.image.height,
          scale: this.image.scale || 2, realism: "none", engine: "standard"
        } });
        if (!estimate.data.trial && Number(estimate.data.credits) > 0 &&
            !window.confirm(`Upscale without realism for ${estimate.data.credits} credits?`)) {
          return;
        }
        const response = await apiClient.post(
          `/apps/upscale/images/${this.imageId}/run?realism=none&engine=standard`,
          null,
          { headers: workFundingHeaders(estimate.data) }
        );
        this.failureMessage = null;
        this.applyImage(response.data.image || response.data);
      } catch (e) {
        if (e.response && e.response.status === 402) {
          const detail = e.response.data && e.response.data.detail;
          this.outOfCreditsMessage =
            (detail && (detail.message || detail)) ||
            "You're out of credits. Top up or upgrade your plan to continue.";
        } else {
          const detail = e.response && e.response.data && e.response.data.detail;
          this.failureMessage =
            (e.workPending &&
              "We couldn't confirm the retry yet. Try again to resume it safely.") ||
            (detail && (detail.message || detail)) ||
            "Something went wrong while restarting the upscale.";
        }
      } finally {
        this.fallbackRunning = false;
      }
    },

    // ---------------------------------------------------------------
    // Files (authenticated endpoints → blob object URLs, since <img>
    // can't carry an Authorization header)
    // ---------------------------------------------------------------
    async loadOriginal() {
      try {
        const blob = await fetchFileBlob(this.resourceClient(),
          `/apps/upscale/images/${this.imageId}/file`,
          { params: { which: "original" } }
        );
        if (this.originalBlobUrl) URL.revokeObjectURL(this.originalBlobUrl);
        this.originalBlobUrl = URL.createObjectURL(blob);
      } catch (e) {
        // No original available - the loader falls back to its placeholder.
      }
    },
    async loadBlobs() {
      if (this.blobsLoading) return;
      this.blobsLoading = true;
      this.resultLoadPct = null;
      try {
        const [originalBlob, resultBlob] = await Promise.all([
          fetchFileBlob(this.resourceClient(), `/apps/upscale/images/${this.imageId}/file`, {
            params: { which: "original" }
          }),
          fetchFileBlob(this.resourceClient(), `/apps/upscale/images/${this.imageId}/file`, {
            params: { which: "result" },
            onDownloadProgress: event => {
              if (event && event.total) {
                this.resultLoadPct = Math.min(100, (100 * event.loaded) / event.total);
              }
            }
          })
        ]);
        this.revokeObjectUrls();
        this.originalBlobUrl = URL.createObjectURL(originalBlob);
        this.resultBlobUrl = URL.createObjectURL(resultBlob);
      } catch (e) {
        this.failureMessage = "The result finished but couldn't be loaded.";
      } finally {
        this.blobsLoading = false;
        this.resultLoadPct = null;
      }
    },
    activeWorkKey() {
      return "upscale:image:" + this.imageId;
    },
    registerActiveWork() {
      // Landing-handoff guests poll through an isolated client the shell
      // cannot use, so their conversion stays view-local.
      if (this.guestProof) return;
      this.$store.commit("SET_ACTIVE_WORK", {
        key: this.activeWorkKey(),
        statusUrl: `/apps/upscale/images/${this.imageId}`,
        statusField: "status",
        readyStatuses: ["complete"],
        failStatuses: ["failed", "cancelled", "needs_credits"],
        route: `/images/${this.imageId}`,
        readyText: "Your image is ready!",
        failText: "Your conversion didn't finish - tap View for details."
      });
    },
    async shareResult() {
      if (!this.originalBlobUrl || !this.resultBlobUrl || this.sharing) return;
      this.sharing = true;
      try {
        const blob = await composeBeforeAfter(this.originalBlobUrl, this.resultBlobUrl);
        const name = this.resultFilename().replace(/\.[^.]+$/, "") + "_before_after.jpg";
        const file = new File([blob], name, { type: "image/jpeg" });
        if (navigator.canShare && navigator.canShare({ files: [file] })) {
          await navigator.share({
            files: [file],
            title: "PhotoRealify",
            text: "Made real with PhotoRealify - photorealify.com"
          });
          return;
        }
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = name;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
      } catch (e) {
        // Cancelled share sheets land here too - nothing to report.
      } finally {
        this.sharing = false;
      }
    },
    viewFullImage() {
      const compare = this.$refs.compare;
      if (compare) compare.goFit();
    },
    viewDetail() {
      const compare = this.$refs.compare;
      if (compare) compare.goDetail();
    },
    openInNewTab() {
      // The enhanced result alone, full resolution, in a plain browser tab.
      if (this.resultBlobUrl) window.open(this.resultBlobUrl, "_blank", "noopener");
    },
    downloadResult() {
      // Every finished result is downloadable, including a guest's free trial
      // conversion; plans are offered when a guest starts another conversion.
      if (!this.resultBlobUrl) return;
      const a = document.createElement("a");
      a.href = this.resultBlobUrl;
      a.download = this.resultFilename();
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    },
    // "photo.png" enhanced to JPEG → "photo_enhanced.jpg" (extension follows
    // the chosen output format, not the original file's).
    resultFilename() {
      const original = (this.image && this.image.original_filename) || "image";
      const stem = original.replace(/\.[^.]+$/, "");
      const settings = this.image && this.image.settings;
      const format = (settings && settings.output_format) || "png";
      return stem + "_enhanced." + (format === "jpeg" ? "jpg" : "png");
    },

    resetState() {
      this.stopPolling();
      this.stopProgressTicker();
      this.progressBase = null;
      this.progressPolledAt = null;
      this.progressPeak = 0;
      this.revokeObjectUrls();
      this.image = null;
      this.loading = true;
      this.notFound = false;
      this.outOfCreditsMessage = null;
      this.failureMessage = null;
    },
    revokeObjectUrls() {
      if (this.originalBlobUrl) URL.revokeObjectURL(this.originalBlobUrl);
      if (this.resultBlobUrl) URL.revokeObjectURL(this.resultBlobUrl);
      this.originalBlobUrl = null;
      this.resultBlobUrl = null;
    }
  }
};
</script>

<style scoped>
.result-meta {
  color: var(--text-secondary);
}

.free-note {
  color: var(--text-secondary);
  padding-top: 0;
}

.compare-hint {
  color: var(--text-secondary);
  font-size: var(--font-size-xs);
  text-align: center;
}

.progress-pct {
  font-size: 11px;
  font-weight: 700;
  color: #fff;
}

/* --- Processing scan loader --------------------------------------- */

.processing-card {
  border-color: var(--accent, #a855f7) !important;
  animation: card-pulse 2.4s ease-in-out infinite;
}

@keyframes card-pulse {
  0%, 100% { box-shadow: 0 0 0 rgba(168, 85, 247, 0); }
  50% { box-shadow: 0 0 18px rgba(168, 85, 247, 0.35); }
}

.scan-wrap {
  position: relative;
  overflow: hidden;
  border-radius: 10px;
  background: var(--bg-secondary, #1b1b25);
}

.scan-image {
  display: block;
  width: 100%;
  max-height: 65vh;
  object-fit: contain;
  filter: brightness(0.55) saturate(0.85);
}

.scan-placeholder {
  width: 100%;
  aspect-ratio: 16 / 9;
}

.scan-shimmer {
  position: absolute;
  inset: 0;
  background: linear-gradient(
    115deg,
    transparent 30%,
    rgba(168, 85, 247, 0.16) 48%,
    rgba(236, 72, 153, 0.16) 52%,
    transparent 70%
  );
  background-size: 220% 100%;
  animation: shimmer-slide 2.6s linear infinite;
}

@keyframes shimmer-slide {
  0% { background-position: 120% 0; }
  100% { background-position: -120% 0; }
}

.scan-line {
  position: absolute;
  left: 0;
  right: 0;
  height: 3px;
  background: linear-gradient(
    90deg,
    transparent,
    var(--accent, #a855f7) 30%,
    #ec4899 70%,
    transparent
  );
  box-shadow: 0 0 14px rgba(168, 85, 247, 0.8);
  animation: scan-sweep 2.2s ease-in-out infinite alternate;
}

@keyframes scan-sweep {
  0% { top: 4%; }
  100% { top: 96%; }
}

.processing-hint {
  color: var(--text-secondary);
}

/* Phones: the three view buttons share one row - no icons, smaller text. */
@media (max-width: 599px) {
  .view-actions {
    flex-wrap: nowrap;
    gap: 6px;
  }

  .view-actions .v-btn {
    flex: 1 1 0;
    min-width: 0 !important;
    height: 26px !important;
    padding: 0 6px !important;
    font-size: 0.65rem !important;
    letter-spacing: 0.02em;
    margin-right: 0 !important;
  }

  .view-actions .v-btn .v-icon {
    display: none;
  }
}
</style>
